import type { NativeModule } from "expo";

import type {
  RemoteInboundMessage,
  RemoteOutboundMessage,
} from "$modules/expo-tv-connect";

export type TvPlatform = "webos" | "tizen" | "mdns";

export type Device = {
  /** Stable id; `platform:name|type|domain` */
  id: string;
  /** Device name */
  name?: string;
  /** Discovery service that found the device */
  platform: TvPlatform;
  /** mDNS TXT (`name`, `code`, `ready`, `v`) */
  txt?: Record<string, string>;
};

export const TV_CONNECT_ERROR_CODE = {
  discoveryFailed: "DISCOVERY_FAILED",
  deviceResolveFailed: "DEVICE_RESOLVE_FAILED",
  deviceConnectionFailed: "DEVICE_CONNECTION_FAILED",
  launchFailed: "LAUNCH_FAILED",
  sendMessageFailed: "SEND_MESSAGE_FAILED",
  pairingRequired: "PAIRING_REQUIRED",
  pairingFailed: "PAIRING_FAILED",
  unknown: "UNKNOWN",
} as const;

export type TvConnectErrorCode =
  (typeof TV_CONNECT_ERROR_CODE)[keyof typeof TV_CONNECT_ERROR_CODE];

export type ErrorEvent = {
  platform: TvPlatform;
  code: TvConnectErrorCode;
  message: string;
  deviceId?: string;
};

export type DiscoveryEvent = {
  platform: TvPlatform;
};

export type MessageEvent = RemoteInboundMessage & {
  platform: TvPlatform;
  deviceId?: string;
};

export type ExpoTvConnectRemoteModuleEvents = {
  onDiscoveryStarted: (event: DiscoveryEvent) => void;
  onDiscoveryStopped: (event: DiscoveryEvent) => void;
  onDeviceAdded: (event: Device) => void;
  onDeviceUpdated: (event: Device) => void;
  onDeviceRemoved: (event: Device) => void;
  onDeviceConnected: (event: Device) => void;
  onDeviceDisconnected: (event: Device) => void;
  onMessage: (event: MessageEvent) => void;
  onError: (event: ErrorEvent) => void;
};

export type StartDiscoveryOptions = {
  /**
   * Services to start. Defaults to all available services.
   */
  platforms?: TvPlatform[];
};

export type StopDiscoveryOptions = {
  /**
   * Services to stop. Defaults to all services.
   */
  platforms?: TvPlatform[];
};

export type ConfigureOptions = {
  clientId: string;
  webOSApplicationId: string;
  tizenApplicationId: string;
  mdnsServiceType: string;
  mdnsDiscoveryDomain: string;
};

export declare class ExpoTvConnectRemoteModule extends NativeModule<ExpoTvConnectRemoteModuleEvents> {
  readonly tizenChannel: string;
  readonly tizenApplicationId: string;
  readonly webOSApplicationId: string;

  // Configuration
  configure(options: ConfigureOptions): void;

  // Discovery
  startDiscovery(options?: StartDiscoveryOptions): Promise<void>;
  stopDiscovery(options?: StopDiscoveryOptions): Promise<void>;
  getDiscoveredDevices(): Promise<Device[]>;

  // Connection
  connect(deviceId: string): Promise<void>;
  disconnect(): Promise<void>;
  getConnectedDevice(): Device | null;

  // Communication
  sendMessage(message: RemoteOutboundMessage): Promise<void>;
}
